import {
  WORK_ORDERS_DETAILS_PROPOSAL_BACK,
  WORK_ORDERS_DETAILS_PROPOSAL_COMPLETION_DATE_UPDATE,
  WORK_ORDERS_DETAILS_PROPOSAL_CREATE_SUCCESS,
  WORK_ORDERS_DETAILS_PROPOSAL_EMAIL_ADD,
  WORK_ORDERS_DETAILS_PROPOSAL_EMAIL_CLEAR,
  WORK_ORDERS_DETAILS_PROPOSAL_EMAIL_MODAL_SHOW_SET,
  WORK_ORDERS_DETAILS_PROPOSAL_EMAIL_REMOVE,
  WORK_ORDERS_DETAILS_PROPOSAL_MARKUP_UPDATE,
  WORK_ORDERS_DETAILS_PROPOSAL_MESSAGE_SET,
  WORK_ORDERS_DETAILS_PROPOSAL_MODAL_CLOSE,
  WORK_ORDERS_DETAILS_PROPOSAL_MODAL_SHOW,
  WORK_ORDERS_DETAILS_PROPOSAL_MODAL_EDIT,
  WORK_ORDERS_DETAILS_PROPOSAL_QUOTE_UPDATE,
  WORK_ORDERS_DETAILS_PROPOSAL_NEXT,
  WORK_ORDERS_DETAILS_PROPOSAL_PDF_VIEWER_CLOSE,
  WORK_ORDERS_DETAILS_PROPOSAL_PRINT_SUCCESS,
  WORK_ORDERS_DETAILS_PROPOSAL_START_DATE_UPDATE,
  WORK_ORDERS_DETAILS_PROPOSAL_SUBJECT_SET,
  WORK_ORDERS_DETAILS_PROPOSAL_USERS_LOAD_SUCCESS,
  WORK_ORDERS_DETAILS_PROPOSAL_DEPOSIT_SELECTED_CHANGE,
  WORK_ORDERS_DETAILS_PROPOSAL_DEPOSIT_COST_CHANGE,
  WORK_ORDERS_DETAILS_PROPOSAL_ADD_BREAKDOWNITEM,
  WORK_ORDERS_DETAILS_PROPOSAL_REMOVE_BREAKDOWNITEM,
  WORK_ORDERS_DETAILS_PROPOSAL_UPDATE_BREAKDOWNITEM,
} from 'actions/admin/work-orders/action-types';
import { showErrorMessage } from 'actions/messages';
import { proposalsApi } from 'services/api/proposals-api';
import {
  addProposal,
  loadTasksFromWorkOrderRequest,
  updateProposal,
  loadWorkOrderRequest,
  selectNavigationBar,
} from 'actions/admin/work-orders/details/details';
import {
  calculateFee,
  calculateInsurace,
  calculateMarkup,
} from 'services/utils/proposal-util';
import { clientsApi } from 'services/api/clients-api';
import { ROLES, usersUtil } from 'services/utils/users-util';
import { closeProposalApprovalModal } from 'actions/admin/work-orders/details/proposal-approval';

export function addEmail(email) {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_EMAIL_ADD,
    payload: email,
  };
}

export function back(view) {
  const previousActions = {
    review: 'form',
  };
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_BACK,
    payload: previousActions[view],
  };
}

export function clearEmails() {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_EMAIL_CLEAR,
  };
}

export function closePdfViewer() {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_PDF_VIEWER_CLOSE,
  };
}

export function closeProposalModal() {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_MODAL_CLOSE,
  };
}

export function createProposalMessage(
  proposalNumber,
  clientName,
  workOrderName = null,
  buildingLocation = null
) {
  const woName = workOrderName ? ` - ${workOrderName}` : '';
  let location = '';
  if (buildingLocation !== null) {
    location = ` at ${buildingLocation.street_address_1}, ${buildingLocation.city} ${buildingLocation.state}`;
  }

  return `Please find the attached proposal #${proposalNumber} for ${clientName}${woName}${location}. If you have any questions, please reach out via chat or email.

Thank you`;
}

export function createProposalSubject(
  workOrderNumber,
  clientName,
  workOrderName = null
) {
  const woName = workOrderName ? ` - ${workOrderName}` : '';
  return `Spackle Proposal for WO #${workOrderNumber} ${clientName}${woName}`;
}

export function continueRequest(
  currentWorkOrderId,
  startDate,
  completionDate,
  valuesByQuoteId,
  qualifications,
  selectedQualificationsById,
  depositCost
) {
  return (dispatch) => {
    const quotesMarkup = Object.entries(valuesByQuoteId).map((element) => {
      const values = element[1];
      return {
        quote_id: element[0],
        fee: values['fee'],
        insurance: values['insurance'],
        markup: values['markup'],
        breakdown_items_attributes: values['breakdownItems'],
      };
    });

    const deposit = depositCost !== null ? { cost: depositCost } : null;

    return proposalsApi
      .create(
        currentWorkOrderId,
        startDate,
        completionDate,
        quotesMarkup,
        qualifications
          .filter((q) => selectedQualificationsById[q.id])
          .map((q) => {
            const value = selectedQualificationsById[q.id];
            return {
              qualification_id: value.id,
              option: value.option !== null ? value.option.label : null,
            };
          }),
        deposit
      )
      .then((response) => {
        if (response.ok) {
          dispatch(continueSuccess());
          dispatch(addProposal(response.proposal));
          dispatch(loadTasksFromWorkOrderRequest(currentWorkOrderId, false)); // fetch tasks because state changes after new proposal
        } else {
          dispatch(showErrorMessage(response.errors));
        }
      });
  };
}

export function continueSuccess() {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_CREATE_SUCCESS,
  };
}

export function initializeEmailValues(
  proposalNumber,
  workOrderNumber,
  workOrderName,
  buildingLocation,
  clientName,
  users
) {
  return (dispatch) => {
    // Create and dispatch generated subject and message
    dispatch(
      setProposalSubject(
        createProposalSubject(workOrderNumber, clientName, workOrderName)
      )
    );
    dispatch(
      setProposalMessage(
        createProposalMessage(
          proposalNumber,
          clientName,
          workOrderName,
          buildingLocation
        )
      )
    );

    // Clear emails
    dispatch(clearEmails());
    // Add client owner by default
    const owner = users.find((user) => usersUtil.hasRole(user, ROLES.OWNER));
    if (owner !== undefined) {
      dispatch(addEmail(owner.email));
    }
  };
}

export function loadUsersSuccess(users) {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_USERS_LOAD_SUCCESS,
    payload: users,
  };
}

export function loadEmailProposalDataRequest(
  proposalNumber,
  workOrderNumber,
  workOrderName,
  buildingLocation,
  clientId,
  clientName
) {
  return (dispatch) => {
    return clientsApi.getUsers(clientId).then((response) => {
      if (response.ok) {
        dispatch(loadUsersSuccess(response.users));
        dispatch(
          initializeEmailValues(
            proposalNumber,
            workOrderNumber,
            workOrderName,
            buildingLocation,
            clientName,
            response.users
          )
        );
      } else {
        dispatch(showErrorMessage(response.errors));
      }

      return response;
    });
  };
}

export function next(view) {
  const nextActions = {
    form: 'review',
    review: 'thank-you',
  };
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_NEXT,
    payload: nextActions[view],
  };
}

export function printProposal(proposalId) {
  return (dispatch) => {
    return proposalsApi.getAsPdf(proposalId).then((response) => {
      if (response.ok) {
        dispatch(printProposalSuccess(response.pdf));
      } else {
        dispatch(showErrorMessage(response.errors));
      }
    });
  };
}

export function printProposalSuccess(pdf) {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_PRINT_SUCCESS,
    payload: pdf,
  };
}

export function removeEmail(email) {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_EMAIL_REMOVE,
    payload: email,
  };
}

export function sendEmail(proposalId, emails, subject, message) {
  return (dispatch) => {
    return proposalsApi
      .sendEmail(proposalId, emails, subject, message)
      .then((response) => {
        if (!response.ok) {
          dispatch(showErrorMessage(response.errors));
        }

        return response;
      });
  };
}

export function setProposalSubject(subject) {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_SUBJECT_SET,
    payload: subject,
  };
}

export function setProposalMessage(message) {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_MESSAGE_SET,
    payload: message,
  };
}

export function setShowEmailModal(value) {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_EMAIL_MODAL_SHOW_SET,
    payload: value,
  };
}

export function showProposalModal(
  quoteEstimateByQuoteId,
  startDate,
  completionDate
) {
  // Calculate markup, fee and insurance for each quote
  const valuesByQuoteId = {};
  Object.entries(quoteEstimateByQuoteId).forEach((entry) => {
    const quoteId = entry[0];
    const estimate = entry[1];

    valuesByQuoteId[quoteId] = {
      markup: Math.round(calculateMarkup(estimate)),
      fee: Math.round(calculateFee(estimate)),
      insurance: Math.round(calculateInsurace(estimate)),
      breakdownItems: [],
    };
  });

  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_MODAL_SHOW,
    payload: { valuesByQuoteId, startDate, completionDate },
  };
}

export function showEditProposalModal(selectedProposal) {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_MODAL_EDIT,
    payload: selectedProposal,
  };
}

export function updateStartDate(value) {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_START_DATE_UPDATE,
    payload: value,
  };
}

export function updateCompletionDate(value) {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_COMPLETION_DATE_UPDATE,
    payload: value,
  };
}

export function updateProposalValue(proposalQuoteId, valueKey, value) {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_QUOTE_UPDATE,
    payload: { proposalQuoteId, key: valueKey, value: value },
  };
}

export function saveProposal(proposal) {
  const values = {
    proposal_quotes: proposal.proposal_quotes,
  };

  return (dispatch) => {
    return proposalsApi.update(proposal.id, values).then((response) => {
      if (response.ok) {
        dispatch(updateProposal(response.proposal));
        dispatch(closeProposalModal());
        dispatch(
          loadWorkOrderRequest(response.proposal.work_order_id, true, false)
        );
        dispatch(selectNavigationBar(response.proposal.work_order_id, 1));
      } else {
        dispatch(showErrorMessage(response.errors));
      }
    });
  };
}

export function updateValue(quoteId, valueKey, value) {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_MARKUP_UPDATE,
    payload: { quoteId, key: valueKey, value: value },
  };
}

export function addBreakdownItem(quoteId, breakdownItem) {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_ADD_BREAKDOWNITEM,
    payload: { quoteId, breakdownItem: breakdownItem },
  };
}

export function removeBreakdownItem(quoteId, biIndex) {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_REMOVE_BREAKDOWNITEM,
    payload: { quoteId, biIndex },
  };
}

export function updateBreakdownItem(quoteId, biIndex, key, value) {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_UPDATE_BREAKDOWNITEM,
    payload: { quoteId, biIndex, key, value },
  };
}

export function withDrawProposalRequest(proposalId) {
  return (dispatch) => {
    return proposalsApi.withdraw(proposalId).then((response) => {
      if (response.ok) {
        dispatch(updateProposal(response.proposal));
        dispatch(closeProposalApprovalModal());
        dispatch(
          loadWorkOrderRequest(response.proposal.work_order_id, true, false)
        );
        dispatch(selectNavigationBar(response.proposal.work_order_id, 1));
      } else {
        dispatch(showErrorMessage(response.errors));
      }
    });
  };
}

export function depositSelectedChange(value) {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_DEPOSIT_SELECTED_CHANGE,
    payload: value,
  };
}

export function depositCostChange(value) {
  return {
    type: WORK_ORDERS_DETAILS_PROPOSAL_DEPOSIT_COST_CHANGE,
    payload: value,
  };
}



// WEBPACK FOOTER //
// ./src/actions/admin/work-orders/details/proposal.js